import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, isSuperAdmin } from '@/hooks/useAuth';
import useIsDesktop from '@/hooks/useIsDesktop';
import { useOutfitReminders } from '@/hooks/useOutfitReminders';
import { useBrand } from '@/hooks/useBrand';
import { listFiles, uploadToSeaweedFS, deleteFile, type SeaweedFile } from '@/lib/seaweedfs';
import ClientDashboardNavbar from './components/ClientDashboardNavbar';
import ClientSidebar from './components/ClientSidebar';
import ClientMobileDrawer from './components/ClientMobileDrawer';
import ClosetTab from './components/ClosetTab';
import OutfitBuilderTab from './components/OutfitBuilderTab';
import ClientChatTab from './components/ClientChatTab';
import ClientPrivateMessagesTab from './components/ClientPrivateMessagesTab';
import LookDuJour from './components/LookDuJour';
import OverviewTab from './components/OverviewTab';
import StudySpaceTab from './components/StudySpaceTab';
import DownloadsTab from './components/DownloadsTab';
import OrdersTab from './components/OrdersTab';
import MyCoursesTab from './components/MyCoursesTab';
import StoreBalancesTab from './components/StoreBalancesTab';
import ClientWalletTab from './components/ClientWalletTab';
import PaymentsTab from './components/PaymentsTab';
import BesoinsPanel from '@/components/feature/BesoinsPanel';
import MissionsPanel from '@/components/feature/MissionsPanel';
import CreatorEarningsTab from './components/CreatorEarningsTab';
import PacksTab from './components/PacksTab';
import ClientAppStoreTab from './components/ClientAppStoreTab';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import ClientAssistantDock from '@/components/feature/ClientAssistantDock';
import FavoritesTab from './components/FavoritesTab';
import ProfileLocationCard from './components/ProfileLocationCard';
import CreditRemindersBanner from '@/components/feature/CreditRemindersBanner';
import TenantColorBridge from '@/components/feature/TenantColorBridge';

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function FileIcon({ type }: { type: SeaweedFile['type'] }) {
  if (type === 'image') return <i className="ri-image-line text-lg text-emerald-500"></i>;
  if (type === 'video') return <i className="ri-video-line text-lg text-amber-500"></i>;
  return <i className="ri-file-3-line text-lg text-foreground-400"></i>;
}

export default function ClientDashboardPage() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop(768);
  const { upcomingCount, todayOutfit, todayLoading } = useOutfitReminders();

  // Onglet d'arrivée pilotable par l'URL (ex. /mon-compte/missions, ?tab=orders).
  const { tab: routeTab } = useParams<{ tab?: string }>();
  const [searchParams] = useSearchParams();
  const requestedTab = (routeTab || searchParams.get('tab') || '').trim();
  const allowedTabs = ['overview', 'etudes', 'files', 'downloads', 'closet', 'outfits', 'planning', 'chat', 'messages', 'profile', 'orders', 'payments', 'store', 'wallet', 'favorites', 'besoins', 'missions', 'revenues', 'courses', 'packs', 'appstore', 'mes-applis'] as const;
  type ClientTab = (typeof allowedTabs)[number];
  const initialTab: ClientTab = (allowedTabs as readonly string[]).includes(requestedTab)
    ? (requestedTab as ClientTab)
    : 'overview';

  const [activeTab, setActiveTab] = useState<ClientTab>(initialTab);

  // Le lien peut changer d'onglet après le montage (navigation interne).
  useEffect(() => {
    if ((allowedTabs as readonly string[]).includes(requestedTab)) {
      setActiveTab(requestedTab as ClientTab);
    }
  }, [requestedTab]);
  const [files, setFiles] = useState<SeaweedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Incrémenté pour demander au chat IA d'afficher le panier depuis la barre de navigation.
  const [cartSignal, setCartSignal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isDesktop) setMobileMenuOpen(false);
  }, [isDesktop]);

  const fetchFiles = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const prefix = `my-files/${user.id}/`;
      const result = await listFiles(prefix);
      setFiles(result.files);
    } catch {
      setError('Impossible de charger vos fichiers.');
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  if (!user) {
    navigate('/login-client', { replace: true });
    return null;
  }

  // Un super admin (typecompte 0 ou 1) qui tombe sur /mon-compte est redirigé
  // vers son espace superadmin au lieu de l'espace client.
  if (isSuperAdmin(user.typecompte)) {
    navigate('/superadmin', { replace: true });
    return null;
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8 = new Uint8Array(arrayBuffer);
      const folder = `my-files/${user.id}`;
      await uploadToSeaweedFS(uint8, file.name, file.type, folder);
      await fetchFiles();
    } catch {
      setError('Erreur lors du téléchargement.');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (file: SeaweedFile) => {
    setError(null);
    try {
      await deleteFile(file.key);
      setFiles((prev) => prev.filter((f) => f.key !== file.key));
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Ouvre le panier depuis l'intérieur du compte : on bascule sur le chat IA
  // (où vit le panier) et on lui demande d'afficher le panier.
  const handleOpenCart = () => {
    setActiveTab('chat');
    setMobileMenuOpen(false);
    navigate('/mon-compte/chat');
    setCartSignal((s) => s + 1);
  };

  const filtered = files.filter((f) =>
    f.filename.toLowerCase().includes(search.toLowerCase())
  );

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const totalLabel = totalSize < 1024
    ? `${totalSize} o`
    : totalSize < 1024 * 1024
      ? `${(totalSize / 1024).toFixed(0)} Ko`
      : `${(totalSize / (1024 * 1024)).toFixed(1)} Mo`;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <TenantColorBridge />
      <ClientDashboardNavbar
        onMenuToggle={() => setMobileMenuOpen((v) => !v)}
        menuOpen={mobileMenuOpen}
        showMenuButton={!isDesktop}
        onOpenCart={handleOpenCart}
      />

      <main className="flex-1 pb-16 lg:pb-0">
        <CreditRemindersBanner />
        <div className="flex items-stretch min-h-[calc(100vh-4rem)]">
          {/* Tiroir de navigation (petit écran uniquement) */}
          <div className="zifek-account-mobile-only">
            <ClientMobileDrawer
              open={mobileMenuOpen}
              user={user}
              brandName={brand.name}
              activeTab={activeTab}
              fileCount={files.length}
              upcomingCount={upcomingCount}
              onClose={() => setMobileMenuOpen(false)}
              onLogout={handleLogout}
            />
          </div>

          {/* Sidebar — desktop. Toujours rendue : le CSS décide de l'affichage,
              ce qui évite que le menu disparaisse sur ordinateur. */}
          <ClientSidebar
            user={user}
            brandName={brand.name}
            activeTab={activeTab}
            fileCount={files.length}
            upcomingCount={upcomingCount}
            onLogout={handleLogout}
          />

          {/* Main content */}
          <div className={activeTab === 'chat'
            ? 'flex-1 flex flex-col min-h-0 h-[calc(100vh-4rem)] p-3 md:p-4'
            : 'flex-1 p-4 md:p-6 lg:p-8 bg-background-50/40'}>
            {activeTab === 'overview' && (
              <OverviewTab user={user} onOpenChat={() => setActiveTab('chat')} onOpenTab={(t) => setActiveTab(t)} />
            )}

            {activeTab === 'etudes' && <StudySpaceTab />}

            {activeTab === 'orders' && <OrdersTab user={user} />}

            {activeTab === 'courses' && <MyCoursesTab user={user} />}

            {activeTab === 'payments' && <PaymentsTab user={user} />}

            {activeTab === 'besoins' && (
              <BesoinsPanel user={user} contactHref="/mon-compte/messages" />
            )}

            {activeTab === 'missions' && (
              <MissionsPanel user={user} typecompte={user.typecompte} detailBasePath="/mon-compte/missions" />
            )}

            {activeTab === 'packs' && <PacksTab user={user} />}

            {activeTab === 'appstore' && <ClientAppStoreTab user={user} mode="store" />}

            {activeTab === 'mes-applis' && <ClientAppStoreTab user={user} mode="installed" />}

            {activeTab === 'revenues' && <CreatorEarningsTab />}

            {['files', 'closet', 'outfits'].includes(activeTab) && (
              <LookDuJour outfit={todayOutfit} loading={todayLoading} />
            )}

            {activeTab === 'files' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                      Mes fichiers
                    </h2>
                    <p className="text-xs text-foreground-500 mt-1">
                      {files.length} fichier{files.length !== 1 ? 's' : ''} &middot; {totalLabel}
                    </p>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-40 transition-all"
                  >
                    <i className={`ri-${uploading ? 'loader-4-line animate-spin' : 'upload-line'} text-sm`}></i>
                    {uploading ? 'Téléchargement...' : 'Ajouter un fichier'}
                  </button>
                  <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" />
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 text-xs rounded-lg mb-4">
                    <i className="ri-error-warning-line"></i>
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto cursor-pointer hover:text-red-900">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                )}

                {files.length > 0 && (
                  <div className="relative mb-5 max-w-md">
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Rechercher un fichier..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/40 rounded-lg text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 transition-colors"
                    />
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/30 rounded-xl">
                    <div className="w-14 h-14 rounded-2xl bg-foreground-100 flex items-center justify-center mb-4">
                      <i className="ri-upload-cloud-2-line text-xl text-foreground-500"></i>
                    </div>
                    <h3 className="text-foreground-800 font-semibold text-sm mb-1">
                      {search ? 'Aucun résultat' : 'Aucun fichier'}
                    </h3>
                    <p className="text-foreground-400 text-xs mb-5">
                      {search ? 'Aucun fichier ne correspond à votre recherche.' : 'Ajoutez vos fichiers — vêtements, documents, images...'}
                    </p>
                    {!search && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2.5 border border-foreground-200 text-foreground-700 rounded-full text-xs font-medium whitespace-nowrap hover:bg-foreground-50 transition-colors cursor-pointer"
                      >
                        Ajouter mon premier fichier
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {filtered.map((file) => (
                      <div key={file.key} className="bg-background-50 border border-background-200/30 rounded-lg overflow-hidden group hover:border-foreground-200/50 transition-all">
                        <div className="aspect-square bg-background-100 flex items-center justify-center relative cursor-pointer" onClick={() => window.open(file.url, '_blank')}>
                          {file.type === 'image' ? (
                            <img src={file.url} alt={file.filename} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          ) : (
                            <div className="flex flex-col items-center gap-1.5">
                              <FileIcon type={file.type} />
                              <span className="text-[10px] text-foreground-400 uppercase font-medium">{file.extension}</span>
                            </div>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-foreground-900/80 text-background-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-foreground-900"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                        <div className="p-2.5">
                          <p className="text-xs font-medium text-foreground-800 truncate">{file.filename}</p>
                          <p className="text-[10px] text-foreground-400">{file.size < 1024 ? `${file.size} o` : file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} Ko` : `${(file.size / (1024 * 1024)).toFixed(1)} Mo`} &middot; {formatDate(file.lastModified)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'downloads' && <DownloadsTab user={user} />}

            {activeTab === 'store' && <StoreBalancesTab />}

            {activeTab === 'wallet' && <ClientWalletTab onOpenChat={() => setActiveTab('chat')} />}

            {activeTab === 'favorites' && <FavoritesTab />}

            {activeTab === 'closet' && <ClosetTab />}

            {activeTab === 'outfits' && <OutfitBuilderTab />}

            {activeTab === 'chat' && <ClientChatTab openCartSignal={cartSignal} />}

            {activeTab === 'messages' && <ClientPrivateMessagesTab />}

            {activeTab === 'profile' && (
              <div className="max-w-3xl xl:max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  Mon profil
                </h2>

                <div className="bg-background-50 border border-background-200/30 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-4 p-6 bg-background-100/50 border-b border-background-200/30">
                    <div className="w-16 h-16 rounded-full bg-foreground-100 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-background-50">
                      {user.image ? (
                        <img src={user.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-foreground-600 font-bold text-xl">{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-900">{user.name}</h3>
                      <p className="text-xs text-foreground-500">@{user.user_name}</p>
                      <p className="text-xs text-foreground-400">{user.email}</p>
                    </div>
                  </div>

                  <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Nom complet</label>
                      <p className="text-sm text-foreground-800 font-medium">{user.name || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Email</label>
                      <p className="text-sm text-foreground-800 font-medium">{user.email || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Nom d'utilisateur</label>
                      <p className="text-sm text-foreground-800 font-medium">@{user.user_name}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Téléphone</label>
                      <p className="text-sm text-foreground-800 font-medium">{user.telephone || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Type de compte</label>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-foreground-100/70 text-foreground-700 rounded-full text-xs font-medium">
                        <i className="ri-user-smile-line text-xs"></i>
                        Client {brand.name}
                      </span>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Membre depuis</label>
                      <p className="text-sm text-foreground-800 font-medium">{formatDate(user.datecreation) || '—'}</p>
                    </div>
                  </div>
                </div>
                <ProfileLocationCard user={user} />
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Chat IA flottant : accessible depuis n'importe quel onglet du compte */}
      <ClientAssistantDock />

      {/* Navigation mobile (barre inférieure) — petit écran uniquement */}
      <div className="zifek-account-mobile-only">
        <MobileBottomNav
          items={[
            { icon: 'ri-dashboard-line', label: 'Compte', to: '/mon-compte/overview', active: activeTab === 'overview' },
            { icon: 'ri-sticky-note-line', label: 'Notes', to: '/mon-compte/apps/zifek-notes', active: false },
            { icon: 'ri-shopping-bag-3-line', label: 'Achats', to: '/mon-compte/orders', active: activeTab === 'orders' },
            { icon: 'ri-megaphone-line', label: 'Missions', to: '/mon-compte/missions', active: activeTab === 'missions' },
            { icon: 'ri-chat-smile-3-line', label: 'Messages', to: '/mon-compte/messages', active: activeTab === 'messages' },
          ]}
          onMore={() => setMobileMenuOpen(true)}
          moreLabel="Plus"
        />
      </div>
    </div>
  );
}